import { FC, useEffect, useState, useRef } from "react";
import CommonLayout from "./CommonLayout";
import FormItem from "./FormItem";
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentHolidayPackage, setHolidayPackage } from "application/features/holidayPackage/holidayPackageSlice";
import { useUploadHolidayPackageCoverMutation, useUploadHolidayPackageGalleryImageMutation } from "application/features/holidayPackage/holidayPackageApiSlice";
import type { HolidayPackage } from "data/types/holidayPackage";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";


export interface AddHolidayPackage2Props { }

const PageAddHolidayPackage2: FC<AddHolidayPackage2Props> = () => {

    const { t, ready } = useTranslation("translation");
    const holidayPackage: HolidayPackage = useSelector(selectCurrentHolidayPackage);
    const [updateCover, { isLoading: uploadingCover }] = useUploadHolidayPackageCoverMutation()
    const [updateGalleryImage, { isLoading: uploadingGalleryImage }] = useUploadHolidayPackageGalleryImageMutation()
    const dispatch = useDispatch()

    const coverRef = useRef<HTMLInputElement>(null);
    const [galleryImages, setGalleryImages] = useState<Array<string>>([]);

    const uploadCover = async (e: any) => {
        try {
        // if file size is greater than 10mb throw error
        if (e.target.files[0].size > 10485760) {
            throw alert('Inserisci un file di dimensioni inferiori a 10mb')
        }
        const formData = new FormData()
        formData.append('holiday-package-cover', e.target.files[0])
    
        const response = await updateCover(formData).unwrap();
        dispatch(setHolidayPackage({ ...holidayPackage, cover_image: response.result.s3Location }))
        } catch (err) {
            console.log(err)
        }
    };

    const uploadGalleryImage = async (e: any) => {
        try {
            const files = Array.from(e.target.files) as File[];

            // Validate all files
            const oversizedFiles = files.filter((file: File) => file.size > 10485760);
            if (oversizedFiles.length > 0) {
                alert(`${oversizedFiles.length} file(s) exceed 10MB limit and will be skipped`);
            }

            const validFiles = files.filter((file: File) => file.size <= 10485760);

            // Create preview URLs for all files
            const previewUrls = validFiles.map((file: File) => URL.createObjectURL(file));
            setGalleryImages([...galleryImages, ...previewUrls]);

            // Upload all files sequentially and accumulate results
            let uploadedImages = [...(holidayPackage?.gallery_images || [])];

            for (const file of validFiles) {
                const formData = new FormData();
                formData.append('holiday-package-gallery-image', file);

                const response = await updateGalleryImage(formData).unwrap();
                uploadedImages.push(response.result.s3Location);

                dispatch(setHolidayPackage({
                    ...holidayPackage,
                    gallery_images: [...uploadedImages]
                }));
            }
        } catch (err) {
            console.log(err)
        }
    };

    const _updateDefaultFields = async () => {
        dispatch(setHolidayPackage({ 
            ...holidayPackage, 
            cover_image: holidayPackage?.cover_image? holidayPackage.cover_image : "", 
            gallery_images: holidayPackage?.gallery_images? holidayPackage.gallery_images : [],
        }))
    }

    useEffect(() => {
        _updateDefaultFields();
    }, [])

    function nextPageCondition() {
        return holidayPackage?.cover_image?.length > 0 && !uploadingCover && !uploadingGalleryImage
    }

    const handleRemoveCover = () => {
        if (coverRef.current) {
          coverRef.current.value = "";
        }
        dispatch(setHolidayPackage({ ...holidayPackage, cover_image: null }))
    }

    const CustomPicker = (url: string) => {
    if (url && url.length > 0) {
        return (
            <div>
                <img src={url} alt="" className="w-auto max-h-80" />
                <div className="absolute top-0 right-0 z-50">
                    <div
                        role="button"
                        aria-label="remove-cover"
                        onClick={() => {
                            handleRemoveCover()
                        }}
                        className="bg-neutral-100 dark:bg-neutral-800 rounded-full p-1"
                    >
                        <svg
                            className="w-4 h-4 text-neutral-6000 dark:text-neutral-300"
                            stroke="currentColor"
                            fill="none"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                        >
                            <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M6 18L18 6M6 6l12 12"
                            ></path>
                        </svg>
                    </div>
                </div>
            </div>
        )
    }
    else {
        return (
            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-neutral-300 dark:border-neutral-6000 border-dashed rounded-md">
                <div className="space-y-1 text-center relative">
                    <svg
                        className="mx-auto h-12 w-12 text-neutral-400"
                        stroke="currentColor"
                        fill="none"
                        viewBox="0 0 48 48"
                        aria-hidden="true"
                    >
                        <path
                            d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        ></path>
                    </svg>
                    <div className="flex text-sm text-neutral-6000 dark:text-neutral-300">
                        <label
                            htmlFor="file-upload"
                            className="relative flex justify-center cursor-pointer text-center  rounded-md font-medium text-primary-6000 hover:text-primary-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500"
                        >
                            {t("translation:components.holidayPackageForm.page2.form.actionDescription")}
                        </label>
                    </div>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">{t("translation:components.holidayPackageForm.page2.form.fileRules")}</p>
                </div>
            </div>
        )
    }
  }


    if (!ready) return null;

    return (
        <CommonLayout
            index="02"
            backtHref="/host/new-holiday-package/add-listing-1"
            nextHref={"/host/new-holiday-package/add-listing-3"}
            canGoNext={nextPageCondition()}
        >
        <>
            <h2 className="text-2xl font-semibold">{t("translation:components.holidayPackageForm.page2.title")}</h2>
            <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>
            {/* FORM */}
            <div className="">    
                <div className="flex flex-row align-center items-center justify-between">
                    <div className="w-full space-y-12">
                        <FormItem
                            label={t("translation:components.holidayPackageForm.page2.form.coverTitle")}
                            desc={""}
                        >
                            <div className="mt-5 relative ">
                                {
                                    uploadingCover ?
                                        <div className="w-full h-32 ">
                                            <Skeleton className="h-full rounded-full" />
                                        </div>
                                        :
                                        CustomPicker(holidayPackage?.cover_image)
                                }
                                <input
                                    disabled={uploadingCover}
                                    type="file"
                                    ref={coverRef}
                                    accept="image/*"
                                    name="holiday-package-cover"
                                    className="absolute w-full h-full z-40 opacity-0 top-0"
                                    onChange={(e) => e.target.files && uploadCover(e)}
                                />
                            </div>
                        </FormItem>

                        <FormItem
                            label={t("translation:components.holidayPackageForm.page2.form.albumTitle")}
                            desc={""}
                        >
                            <div className="mt-5 relative">
                                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-neutral-300 dark:border-neutral-6000 border-dashed rounded-md">
                                    <div className="space-y-1 text-center">
                                        <svg
                                            className="mx-auto h-12 w-12 text-neutral-400"
                                            stroke="currentColor"
                                            fill="none"
                                            viewBox="0 0 48 48"
                                            aria-hidden="true"
                                        >
                                            <path
                                            d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            ></path>
                                        </svg>
                                        <div className="flex text-sm text-neutral-6000 dark:text-neutral-300">
                                            <label
                                                htmlFor="file-upload-2"
                                                className="relative cursor-pointer  rounded-md font-medium text-primary-6000 hover:text-primary-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500"
                                            >
                                                {t("translation:components.holidayPackageForm.page2.form.actionDescription")}
                                            </label>
                                        </div>
                                        <p className="text-xs text-neutral-500 dark:text-neutral-400">
                                            {t("translation:components.holidayPackageForm.page2.form.fileRules")}
                                        </p>
                                    </div>
                                </div>
                                <input
                                    id="file-upload-2"
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    name="holiday-package-gallery-images"
                                    className="absolute w-full h-full z-40 opacity-0 top-0"
                                    onChange={(e) => e.target.files && uploadGalleryImage(e)}
                                />
                            </div>

                            <div className="w-full mt-2">
                                {
                                    uploadingGalleryImage ?
                                        <div className="w-full h-32 ">
                                            <Skeleton className="h-full rounded-full" />
                                        </div>
                                        :
                                        <div className="grid grid-cols-3 gap-4 mt-4">
                                            {
                                                holidayPackage?.gallery_images?.map((item: string, index: number) => (
                                                <div key={item || `gallery-${index}`} className="relative">
                                                    <img src={item} alt="" className="w-full h-32 object-cover rounded-md" />
                                                    <div className="absolute top-0 right-0">
                                                        <div
                                                            onClick={() => {
                                                                const updatedGallery = holidayPackage.gallery_images.filter((i: string) => i !== item)
                                                                dispatch(setHolidayPackage({ ...holidayPackage, gallery_images: updatedGallery }))
                                                            }}
                                                            className="bg-neutral-100 dark:bg-neutral-800 rounded-full p-1"
                                                        >
                                                            <svg
                                                                className="w-4 h-4 text-neutral-6000 dark:text-neutral-300"
                                                                stroke="currentColor"
                                                                fill="none"
                                                                viewBox="0 0 24 24"
                                                                aria-hidden="true"
                                                            >
                                                                <path
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                    strokeWidth="2"
                                                                    d="M6 18L18 6M6 6l12 12"
                                                                ></path>
                                                            </svg>
                                                        </div>
                                                    </div>
                                                </div>
                                                ))
                                            }
                                        </div>
                                }
                            </div>

                        </FormItem>
                    </div>       
                </div>
            </div>
        </>
        </CommonLayout >
    );
};

export default PageAddHolidayPackage2;
